import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Save, X, LogOut, BookOpen, Eye, EyeOff } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  id: "",
  slug: "",
  category: "Holiday Homes",
  title: "",
  summary: "",
  bodyText: "", // markdown-like simple text
  published: true,
};

const categoryOptions = ["Holiday Homes", "Plot Investment", "Investment Basics", "Market Updates"];

// Simple block parser: blank-line-separated paragraphs.
// Lines starting with "## " become h3, "- " become list items.
function textToBlocks(text) {
  const blocks = [];
  const sections = text.split(/\n\s*\n/);
  for (const sec of sections) {
    const trimmed = sec.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith("## ")) {
      blocks.push({ type: "h3", text: trimmed.replace(/^##\s*/, "") });
    } else if (trimmed.split("\n").every((l) => l.trim().startsWith("- "))) {
      blocks.push({ type: "list", items: trimmed.split("\n").map((l) => l.replace(/^-\s*/, "").trim()) });
    } else {
      blocks.push({ type: "p", text: trimmed });
    }
  }
  return blocks;
}

function blocksToText(blocks) {
  if (!blocks || !blocks.length) return "";
  return blocks
    .map((b) => {
      if (b.type === "h3") return `## ${b.text}`;
      if (b.type === "p") return b.text;
      if (b.type === "h4") return `## ${b.text}`;
      if (b.type === "list") return (b.items || []).map((i) => `- ${i}`).join("\n");
      return "";
    })
    .join("\n\n");
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("zentry_admin_token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const authHeaders = { headers: { "X-Admin-Token": token } };

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { token: tokenInput });
      localStorage.setItem("zentry_admin_token", tokenInput);
      setToken(tokenInput);
      toast.success("Logged in.");
    } catch {
      toast.error("Invalid admin token.");
    }
  };

  const logout = () => {
    localStorage.removeItem("zentry_admin_token");
    setToken("");
    setItems([]);
    setEditing(null);
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/insights`, authHeaders);
      setItems(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Token expired. Please login again.");
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startNew = () => setEditing({ ...empty });
  const startEdit = (it) =>
    setEditing({
      id: it.id,
      slug: it.slug,
      category: it.category,
      title: it.title,
      summary: it.summary,
      bodyText: blocksToText(it.body),
      published: it.published,
    });

  const save = async () => {
    if (!editing.title || !editing.slug || !editing.summary) {
      toast.error("Title, slug, summary are required.");
      return;
    }
    const payload = {
      slug: editing.slug.trim().toLowerCase(),
      category: editing.category,
      title: editing.title.trim(),
      summary: editing.summary.trim(),
      body: textToBlocks(editing.bodyText || ""),
      published: editing.published,
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/insights/${editing.id}`, payload, authHeaders);
        toast.success("Updated.");
      } else {
        await axios.post(`${API}/admin/insights`, payload, authHeaders);
        toast.success("Created.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await axios.delete(`${API}/admin/insights/${id}`, authHeaders);
      toast.success("Deleted.");
      await load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Zentry Admin</div>
        <h1 className="font-display text-4xl mt-4">Sign in</h1>
        <p className="text-zinc-600 mt-3 text-sm">Enter your admin token to manage Insights articles.</p>
        <form onSubmit={login} className="mt-8 space-y-4 bg-white border border-zinc-200 rounded-2xl p-6">
          <div>
            <Label>Admin Token</Label>
            <Input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              className="mt-2"
              placeholder="Your admin token"
              data-testid="admin-token-input"
            />
          </div>
          <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-login-submit">
            Sign in
          </button>
          <p className="text-xs text-zinc-500">
            The default admin token is <code className="bg-zinc-100 px-1 rounded">zentry-admin-2026</code>. Change it via the
            <code className="bg-zinc-100 px-1 rounded ml-1">ADMIN_TOKEN</code> environment variable.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Zentry Admin</div>
          <h1 className="font-display text-4xl mt-2">Insights CMS</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={startNew} className="btn-primary" data-testid="admin-new"><Plus className="h-4 w-4" /> New article</button>
          <button onClick={logout} className="btn-secondary" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </div>

      {editing && (
        <div className="mt-8 bg-white border-2 border-[#1D4ED8] rounded-3xl p-8 space-y-4" data-testid="admin-editor">
          <div className="flex items-center justify-between">
            <div className="font-display text-2xl">{editing.id ? "Edit article" : "New article"}</div>
            <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-900"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Title *</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-2" data-testid="admin-title" />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="mt-2" placeholder="kebab-case-url-slug" data-testid="admin-slug" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-2 w-full px-3 py-2 border border-zinc-200 rounded-md text-sm" data-testid="admin-category">
                {categoryOptions.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3 mt-7">
              <input type="checkbox" id="published" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="admin-published" />
              <Label htmlFor="published">Published</Label>
            </div>
          </div>
          <div>
            <Label>Summary *</Label>
            <Textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} className="mt-2" rows={2} data-testid="admin-summary" />
          </div>
          <div>
            <Label>Body</Label>
            <p className="text-xs text-zinc-500 mt-1">Use blank lines between paragraphs. Start a line with <code className="bg-zinc-100 px-1 rounded">##</code> for a heading. Start lines with <code className="bg-zinc-100 px-1 rounded">- </code> for a bullet list.</p>
            <Textarea value={editing.bodyText} onChange={(e) => setEditing({ ...editing, bodyText: e.target.value })} className="mt-2 font-mono text-sm" rows={14} data-testid="admin-body" />
          </div>
          <div className="flex gap-3">
            <button onClick={save} className="btn-primary" data-testid="admin-save"><Save className="h-4 w-4" /> Save article</button>
            <button onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-4">All articles ({items.length})</div>
        {loading && <div className="text-sm text-zinc-500">Loading...</div>}
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-zinc-200 rounded-2xl p-5 flex items-start justify-between gap-4" data-testid={`admin-row-${it.slug}`}>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">{it.category}</div>
                <div className="font-display text-xl mt-1">{it.title}</div>
                <div className="text-sm text-zinc-600 mt-1 line-clamp-2">{it.summary}</div>
                <div className="text-xs text-zinc-500 mt-2 flex items-center gap-3">
                  <span>/{it.slug}</span>
                  <span className="flex items-center gap-1">{it.published ? <><Eye className="h-3 w-3" />Published</> : <><EyeOff className="h-3 w-3" />Draft</>}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/insights/${it.slug}`} target="_blank" className="text-zinc-500 hover:text-[#1D4ED8] p-2"><BookOpen className="h-4 w-4" /></Link>
                <button onClick={() => startEdit(it)} className="text-zinc-500 hover:text-[#1D4ED8] p-2" data-testid={`admin-edit-${it.slug}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(it.id)} className="text-red-500 hover:text-red-700 p-2" data-testid={`admin-delete-${it.slug}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div className="bg-white border border-dashed border-zinc-300 rounded-2xl p-10 text-center text-zinc-500">
              No articles yet. Click "New article" to publish your first one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
